<script setup lang="ts">
import merge from 'lodash-es/merge';
import { Multiselect } from 'vue-multiselect';
import * as yup from 'yup';
import type { PageListItemModel } from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';

interface FormSettings
{
    settings: any;
}

defineOptions({
    name: 'WidgetLinkSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const { $log } = useLogging();
const { $t } = useLocalization();

const layoutContext = inject<ComputedRef<{ websiteId: number; locale: string } | null>>('layoutPageSelectorContext', null);

const module = ref('link');
const defaults = ref<any>({
    pages: [],
    nameIsVisible: true,
    display: '10'
});
const pageOptions = ref<PageListItemModel[]>([]);
const optionsReady = ref(false);

const form = computed<FormType<FormSettings>>(() =>
{
    return props.modelValue;
});

const store = useWebsitesStore();

const displayOptions = computed<Option[]>(() =>
{
    return [
        { value: '3', text: $t('[[[3]]]') },
        { value: '5', text: $t('[[[5]]]') },
        { value: '10', text: $t('[[[10]]]') }
    ];
});

const selectedPageOptions = computed<Option[]>(() =>
{
    return pageOptions.value.map((p) =>
    {
        return { value: p.id.toString(), text: p.option };
    });
});

function effectiveWebsiteId(): number
{
    return layoutContext?.value?.websiteId ?? form.value.websiteId ?? store.current.website;
}

function effectiveLocale(): string
{
    return layoutContext?.value?.locale ?? form.value.locale ?? store.current.locale;
}

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

async function loadPages(): Promise<void>
{
    const websiteId = effectiveWebsiteId();
    const locale = effectiveLocale();

    if (!websiteId || !locale)
        return;

    try
    {
        pageOptions.value = await PagesService.getPagesByModule(websiteId, locale, module.value);
        pageOptions.value = pageOptions.value.filter(x => x.id != 0);
    }
    catch (ex)
    {
        pageOptions.value = [];
        $log.debug(ex);
    }
}

async function loadOptions(): Promise<void>
{
    optionsReady.value = false;
    await loadPages();
    optionsReady.value = true;
}

async function validate(): Promise<void>
{
    form.value.validate(schema => ({
        settings: schema.object({
            pages: yup.array().min(1, $t('[[[Wybranie strony jest wymagane]]]'))
        })
    }));
}

watch(() => form.value.settings, () =>
{
    triggerModel();
}, { deep: true });

watch(() => [form.value.websiteId, form.value.locale, layoutContext?.value?.websiteId, layoutContext?.value?.locale], async () =>
{
    if (!effectiveWebsiteId() || !effectiveLocale())
        return;

    await loadOptions();
});

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);

    if (effectiveWebsiteId() && effectiveLocale())
    {
        await loadOptions();

        return;
    }

    await wait(() => effectiveWebsiteId() && effectiveLocale());
    await loadOptions();
});

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup>
        <IdeoFormCheckbox v-model="form.settings.nameIsVisible">
            {{ $t('[[[Wyświetlaj nazwę bloczka]]]') }}
        </IdeoFormCheckbox>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Wybór listy stron]]]')" :invalid-feedback="form.$errors.first('settings.pages')" :state="form.$errors.state('settings.pages')" required>
        <Multiselect
            v-if="optionsReady"
            v-model="form.settings.pages"
            label="text"
            track-by="value"
            :options="selectedPageOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz strony]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Liczba wyświetlanych najnowszych elementów]]]')" :invalid-feedback="form.$errors.first('settings.display')" :state="form.$errors.state('settings.display')">
        <IdeoFormSelect v-model="form.settings.display" :options="displayOptions" />
    </IdeoFormGroup>
</template>
